import { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, Area, AreaChart } from "recharts";
import { Activity, Zap, Leaf, Users, CircleDot, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Chapter } from "@/components/Reveal";
import { Slider } from "@/components/ui/slider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CENTER = [14.152, -15.752];

const ZONE_META = [
  { id: "solar", pos: [14.155, -15.756], color: "#38BDF8", imgs: ["https://images.unsplash.com/photo-1509391366360-2e959784a276?w=600&q=80", "https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=600&q=80"] },
  { id: "agri", pos: [14.150, -15.748], color: "#4ADE80", imgs: ["https://images.pexels.com/photos/11733080/pexels-photo-11733080.jpeg?auto=compress&cs=tinysrgb&w=600", "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600&q=80"] },
  { id: "fish", pos: [14.147, -15.755], color: "#22D3EE", imgs: ["https://images.unsplash.com/photo-1524704796725-9fc3044a58b2?w=600&q=80", "https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=600&q=80"] },
  { id: "community", pos: [14.156, -15.746], color: "#FBBF24", imgs: ["https://images.unsplash.com/photo-1667844551497-686b8847986f?w=600&q=80", "https://images.unsplash.com/photo-1497486751825-1233686d5d80?w=600&q=80"] },
  { id: "grid", pos: [14.149, -15.760], color: "#A78BFA", imgs: ["https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=600&q=80"] },
];

function markerIcon(color) {
  return L.divIcon({
    className: "",
    html: `<span style="display:flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:50%;background:${color}33;border:1px solid ${color}"><span style="width:10px;height:10px;border-radius:50%;background:${color};box-shadow:0 0 12px ${color}"></span></span>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });
}

function Metric({ icon: Icon, label, value, unit, sub }) {
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-xl p-5" data-testid="metric-card">
      <div className="flex items-center gap-2 text-slate-400">
        <Icon className="w-4 h-4" />
        <span className="text-xs">{label}</span>
      </div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className="mono text-2xl sm:text-3xl text-white">{value}</span>
        <span className="mono text-xs text-sky-400">{unit}</span>
      </div>
      {sub && <div className="mono text-[11px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function Monitoring() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [month, setMonth] = useState([8]);
  const [selected, setSelected] = useState(null);
  const markerRefs = useRef({});

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/monitoring`);
        if (active) setData(res.data);
      } catch (e) { /* keep previous */ }
    };
    load();
    const id = setInterval(load, 5000);
    return () => { active = false; clearInterval(id); };
  }, []);

  const phaseKeys = { land_prep: "p_land", installation: "p_install", grid_connection: "p_grid", commissioning: "p_commission" };
  const m = data?.metrics;
  const nf = (n) => new Intl.NumberFormat(lang === "fr" ? "fr-FR" : "en-US").format(n);

  const openZone = (id) => {
    setSelected(id);
    const mk = markerRefs.current[id];
    if (mk) mk.openPopup();
  };

  return (
    <section id="monitoring" className="py-24 sm:py-32 bg-[#020617] text-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Chapter>{t("monitoring.chapter")}</Chapter>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-white">{t("monitoring.title")}</h2>
            <p className="text-slate-400 mt-4 max-w-xl">{t("monitoring.subtitle")}</p>
          </div>
          <div className="flex items-center gap-2 mono text-xs text-green-400 bg-green-400/10 border border-green-400/20 rounded-full px-4 py-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute h-2 w-2 rounded-full bg-green-400 opacity-75" />
              <span className="relative rounded-full h-2 w-2 bg-green-400" />
            </span>
            {t("monitoring.live")}
          </div>
        </div>

        {/* Metrics */}
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Metric icon={Zap} label={t("monitoring.m_output")} value={m ? nf(m.live_output_mw) : "—"} unit="MW" />
          <Metric icon={Activity} label={t("monitoring.m_installed")} value={m ? nf(m.installed_capacity_mwac) : "—"} unit="MWac" sub={`${t("monitoring.target")}: 250`} />
          <Metric icon={Leaf} label={t("monitoring.m_co2")} value={m ? nf(m.co2_saved_tons) : "—"} unit="t" />
          <Metric icon={Users} label={t("monitoring.m_workforce")} value={m ? nf(m.workforce_on_site) : "—"} unit="" />
        </div>

        <div className="mt-4 grid lg:grid-cols-3 gap-4">
          {/* Progress + phases */}
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <span className="text-sm text-slate-400">{t("monitoring.overall")}</span>
              <span className="mono text-2xl text-sky-400">{data?.overall_progress ?? "—"}%</span>
            </div>
            <div className="h-2 rounded-full bg-slate-800 overflow-hidden mb-8">
              <motion.div className="h-full bg-gradient-to-r from-sky-500 to-green-400"
                initial={{ width: 0 }} whileInView={{ width: `${data?.overall_progress ?? 0}%` }}
                viewport={{ once: true }} transition={{ duration: 1.2, ease: "easeOut" }} />
            </div>
            <div className="space-y-5">
              {(data?.phases ?? []).map((p) => (
                <div key={p.key} data-testid={`phase-${p.key}`}>
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-slate-300">{t(`monitoring.${phaseKeys[p.key]}`)}</span>
                    <span className="mono text-slate-400">{p.progress}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                    <div className="h-full bg-sky-500 rounded-full" style={{ width: `${p.progress}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 24h production chart */}
          <div className="lg:col-span-2 bg-slate-900 border border-slate-700 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-slate-400">{t("monitoring.prod_t")}</span>
              <span className="mono text-xs text-slate-500">{m ? `${nf(m.energy_today_mwh)} MWh` : ""}</span>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data?.production_24h ?? []}>
                  <defs>
                    <linearGradient id="pgrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#38BDF8" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#38BDF8" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="h" tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: "#64748b" }} stroke="#334155" />
                  <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: "#64748b" }} stroke="#334155" />
                  <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #334155", borderRadius: 8, fontFamily: "JetBrains Mono", fontSize: 12 }} />
                  <Area type="monotone" dataKey="mw" stroke="#38BDF8" strokeWidth={2} fill="url(#pgrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Timeline slider */}
        <div className="mt-4 bg-slate-900 border border-slate-700 rounded-xl p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <span className="text-sm text-slate-400 mono uppercase tracking-widest">Timeline</span>
            <span className="mono text-sky-400 text-sm">
              {lang === "fr" ? "Mois" : "Month"} {month[0]} / 12
            </span>
          </div>
          <Slider value={month} onValueChange={setMonth} min={0} max={12} step={1} data-testid="timeline-slider" />
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["p_land", 0, 2], ["p_install", 2, 8], ["p_grid", 8, 11], ["p_commission", 11, 12]].map(([k, s, e], i) => {
              const active = month[0] >= s;
              return (
                <div key={k} className={`rounded-lg border p-3 transition-colors duration-300 ${active ? "border-sky-500 bg-sky-500/10" : "border-slate-700 bg-slate-800/40"}`}>
                  <CircleDot className={`w-4 h-4 mb-2 ${active ? "text-sky-400" : "text-slate-600"}`} />
                  <div className={`text-xs ${active ? "text-white" : "text-slate-500"}`}>{t(`monitoring.${k}`)}</div>
                  <div className="mono text-[10px] text-slate-500 mt-1">M{s}–M{e}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Interactive map */}
        <div className="mt-4 grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-slate-900 border border-slate-700 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-slate-700">
              <span className="text-sm text-slate-300">{t("monitoring.map_t")}</span>
              <span className="mono text-[11px] text-slate-500">{t("monitoring.map_hint")}</span>
            </div>
            <div className="h-[420px]">
              <MapContainer center={CENTER} zoom={15} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
                <TileLayer
                  url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                  attribution='&copy; OpenStreetMap &copy; CARTO'
                />
                {ZONE_META.map((z) => (
                  <Marker
                    key={z.id}
                    position={z.pos}
                    icon={markerIcon(z.color)}
                    ref={(el) => { if (el) markerRefs.current[z.id] = el; }}
                    eventHandlers={{ click: () => setSelected(z.id) }}
                  >
                    <Popup>
                      <div style={{ minWidth: 180 }}>
                        <div style={{ fontWeight: 600, color: "#F8FAFC" }}>{t(`zones.${z.id}.name`)}</div>
                        <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: z.color, marginTop: 2 }}>{t(`zones.${z.id}.type`)}</div>
                        <img src={z.imgs[0]} alt="" style={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 8, marginTop: 8 }} />
                        <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 8 }}>{t(`zones.${z.id}.desc`)}</div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>

          {/* Zone selector + gallery */}
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-6">
            <div className="space-y-2">
              {ZONE_META.map((z) => (
                <button
                  key={z.id}
                  onClick={() => openZone(z.id)}
                  data-testid={`zone-btn-${z.id}`}
                  className={`w-full text-left rounded-lg border px-4 py-3 transition-colors duration-200 flex items-center gap-3 ${selected === z.id ? "border-sky-500 bg-sky-500/10" : "border-slate-700 hover:border-slate-500"}`}
                >
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: z.color, boxShadow: `0 0 10px ${z.color}` }} />
                  <span className="flex-1">
                    <span className="block text-sm text-white">{t(`zones.${z.id}.name`)}</span>
                    <span className="block mono text-[10px] text-slate-500">{t(`zones.${z.id}.type`)}</span>
                  </span>
                </button>
              ))}
            </div>
            <AnimatePresence mode="wait">
              {selected && (
                <motion.div
                  key={selected}
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="mt-4 pt-4 border-t border-slate-700"
                  data-testid="zone-gallery"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white font-medium">{t(`zones.${selected}.name`)}</span>
                    <button onClick={() => setSelected(null)} className="text-slate-500 hover:text-white transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">{t(`zones.${selected}.desc`)}</p>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {ZONE_META.find((z) => z.id === selected).imgs.map((src, i) => (
                      <img key={i} src={src} alt="" className="w-full h-20 object-cover rounded-lg border border-slate-700" />
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
